import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, } from 'react-bootstrap';
import lodash from 'lodash';
import { buildFailureStatus, buildSuccessStatus, } from '../constants/AlertConstants';
import PrepaidAccountService from '../../service/PrepaidAccountService';
import ObjectUtils from '../../modules/ObjectUtils';
import FieldSet from '../fieldset/FieldSet';
import Grid from '../grid/Grid';
import AsyncActionButton from '../button/AsyncActionButton';
import AccountLinkForm from './AccountLinkForm';
import Confirm from '../confirm/Confirm';

class PrepaidAccountForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  onDismiss() {
    return () => {
      this.setState({
        status: null,
      });
    };
  }

  onAccountRowSelect(selectedRow) {
    const { accountRows, } = this.state;

    const account = accountRows.find((row) => {
      const { raw, } = row;
      return raw.id === selectedRow.id;
    });

    this.setState({
      selectedAccount: account.raw,
    });
  }

  onRemoveAutoReload(selectedAccount) {
    this.setState({
      isAutoReloadLoading: true,
    });
    return PrepaidAccountService.removeAutoReloadFromPrepaidAccount(selectedAccount.autoReloadId)
      .then(() => {
        this.setState({
          status: buildSuccessStatus(
            `Successfully removed auto reload for ${selectedAccount.id}`,
            this.onDismiss()
          ),
        });
      })
      .catch(() => {
        this.setState({
          status: buildFailureStatus(
            `Unable to remove auto reload for ${selectedAccount.id}`,
            this.onDismiss()
          ),
        });
      })
      .finally(() => {
        this.setState({
          isAutoReloadLoading: false,
        });
      });
  }

  init() {
    const { serviceOfferingId, externalAccountId, } = this.props;
    if (serviceOfferingId && externalAccountId) {
      this.loadAccounts({ externalAccountId, serviceOfferingId, });
    }
  }

  handleShow() {
    this.init();
    this.setState({
      show: true,
    });
  }

  handleClose() {
    this.setState({
      show: false,
    });
    this.reset();
  }

  reset() {
    this.setState({
      isAutoReloadLoading: false,
      isAutoreloadConfirmOpen: false,
      isTransactionLoading: false,
      accountHeaders: [],
      accountRows: [],
      selectedAccount: null,
      transactionHeaders: [],
      transactionRows: [],
      status: null,
    });
  }

  loadAccounts(criteria) {
    const headers = [];
    const rows = [];
    PrepaidAccountService.getPrepaidAccountsBySearchCriteria(criteria)
      .then((response) => {
        const { accounts, } = response.data;

        if (accounts !== null && accounts.length > 0) {
          const exclusions = value => ![ 'customerIds', ].includes(value);
          const formattedAccounts = accounts.map((account) => {
            const copy = { ...account, };
            copy.customerIds = ObjectUtils.formatStringAsJSONArray(copy.customerIds);
            return ObjectUtils.filterObject(copy, exclusions);
          });

          ObjectUtils.toLargestKeySet(formattedAccounts.map(v => v.value)).forEach((key) => {
            const value = {
              display: lodash.startCase(key),
              value: key,
            };
            headers.push(value);
          });

          formattedAccounts.forEach((account) => {
            rows.push({
              value: Object.values(account.value),
              raw: account.raw,
            });
          });
          this.setState({
            accountHeaders: headers,
            accountRows: rows,
          });
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {});
  }

  loadTransactions(selectedAccount) {
    const headers = [];
    const rows = [];
    this.setState({ isTransactionLoading: true, });
    PrepaidAccountService.getPrepaidTransactionsByAccountId({
      prepaidAccountId: selectedAccount.id,
    })
      .then((response) => {
        const { transactions, } = response.data;

        if (transactions && transactions.length > 0) {
          const formattedAccounts = transactions.map((transaction) => {
            const copy = { ...transaction, };
            return ObjectUtils.filterObject(copy, () => true);
          });

          ObjectUtils.toLargestKeySet(formattedAccounts.map(v => v.value)).forEach((key) => {
            const value = {
              display: lodash.startCase(key),
              value: key,
            };
            headers.push(value);
          });

          formattedAccounts.forEach((account) => {
            rows.push({
              value: Object.values(account.value),
              raw: account.raw,
            });
          });
        }
        this.setState({
          transactionHeaders: headers,
          transactionRows: rows,
        });
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        this.setState({ isTransactionLoading: false, });
      });
  }

  showRemoveAutoReload(selectedAccount) {
    const { isAutoReloadLoading, isAutoreloadConfirmOpen, } = this.state;

    const onYes = () => {
      this.onRemoveAutoReload(selectedAccount).then(() => {
        this.setState({
          isAutoReloadLoading: false,
          isAutoreloadConfirmOpen: false,
        });
      });
    };

    const onNo = () => {
      this.setState({
        isAutoreloadConfirmOpen: false,
      });
    };

    const openAutoreloadConfirmation = () => {
      this.setState({
        isAutoreloadConfirmOpen: true,
      });
    };

    return (
      <div className="m-1">
        <AsyncActionButton
          isLoading={isAutoReloadLoading}
          contents="Remove Auto Reload"
          variant="danger"
          onClick={openAutoreloadConfirmation}
        />
        <Confirm
          open={isAutoreloadConfirmOpen}
          title="Remove Auto Reload"
          content="Are you sure you want to continue?"
          isLoading={isAutoReloadLoading}
          onYes={onYes}
          onNo={onNo}
          onClose={onNo}
        />
      </div>
    );
  }

  showAccountFooter(selectedAccount) {
    const { isTransactionLoading, } = this.state;
    if (!selectedAccount) {
      return null;
    }
    return (
      <div className="container">
        <p>{`Selected ID: ${selectedAccount.id}`}</p>
        <hr />
        <div className="row">
          <div className="m-1">
            <AsyncActionButton
              isLoading={isTransactionLoading}
              contents="Transactions"
              onClick={() => this.loadTransactions(selectedAccount)}
            />
          </div>
          <div className="m-1">
            <AccountLinkForm
              customerIds={selectedAccount.customerIds}
              id={selectedAccount.id}
              serviceOfferingId="ppa_account"
              modalSize="s"
            />
          </div>
          {this.showRemoveAutoReload(selectedAccount)}
        </div>
      </div>
    );
  }

  showBody() {
    const { serviceOfferingId, externalAccountId, } = this.props;
    const {
      accountHeaders = [],
      accountRows = [],
      selectedAccount,
      transactionHeaders = [],
      transactionRows = [],
      status,
    } = this.state;
    if (!serviceOfferingId || !externalAccountId) {
      return null;
    }
    return (
      <div className="container">
        <div>
          {status}
          <FieldSet title="Prepaid Accounts" style={{ maxHeight: '300px', }}>
            <Grid
              headers={accountHeaders}
              rows={accountRows}
              onRowSelect={row => this.onAccountRowSelect(row)}
            />
          </FieldSet>
        </div>
        {this.showAccountFooter(selectedAccount)}
        <div style={{ marginTop: '20px', }}>
          <FieldSet title="Prepaid Transactions" style={{ maxHeight: '300px', }}>
            <Grid headers={transactionHeaders} rows={transactionRows} />
          </FieldSet>
        </div>
      </div>
    );
  }

  render() {
    const { title, } = this.props;
    const { show, } = this.state;
    return (
      <div>
        <Button variant="primary" onClick={() => this.handleShow()}>
          Prepaid Accounts
        </Button>
        <Modal show={show} onHide={() => this.handleClose()} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>{title}</Modal.Title>
          </Modal.Header>
          <Modal.Body>{this.showBody()}</Modal.Body>
        </Modal>
      </div>
    );
  }
}

PrepaidAccountForm.defaultProps = {
  title: 'Prepaid Account Form',
};

PrepaidAccountForm.propTypes = {
  title: PropTypes.string,
  serviceOfferingId: PropTypes.string.isRequired,
  externalAccountId: PropTypes.string.isRequired,
};

export default PrepaidAccountForm;
